<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useCurrency } from 'dashboard/composables/useCurrency';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import KanbanAPI from 'dashboard/api/kanban';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnel: {
    type: Object,
    default: () => ({}),
  },
  // Grupo a renderizar quando usado em abas ('ia' | 'produtos' | 'all').
  section: {
    type: String,
    default: 'all',
  },
});

const emit = defineEmits(['update']);

const showSection = key => props.section === 'all' || props.section === key;

const { t } = useI18n();
const { currency } = useCurrency();

const sender = computed(() => props.conversation?.meta?.sender || {});

// Normalizes date values coming either as Unix seconds (numbers, the shape
// conversation.created_at/last_activity_at come in) or ISO8601 strings —
// mirrors DealDetailsSidebar.vue's normalizeDate helper.
const normalizeDate = value => {
  if (!value) return null;
  if (typeof value === 'number' && value < 4102444800) {
    return new Date(value * 1000);
  }
  return new Date(value);
};

// --- Health panel (AI score + reply/next-step suggestion) -----------------

const intentScore = computed(() => {
  const raw = sender.value?.custom_attributes?.lia_intent_score;
  if (raw === null || raw === undefined || raw === '') return null;
  const score = parseInt(raw, 10);
  return Number.isNaN(score) ? null : score;
});

// Short justification the lead-scoring service stores alongside the score
// (app/services/agno/lead_scoring_service.rb) — the closest real data we
// have to "positive/negative factors"; there is no such factors field.
const intentReasoning = computed(
  () => sender.value?.custom_attributes?.lia_intent_reasoning || ''
);

const suggestionResolved = ref(false);
const resolvingSuggestion = ref(false);

// Suggestion payload shape/parsing + accept/reject wiring mirrors
// KanbanCard.vue's aiSuggestion/acceptSuggestion/ignoreSuggestion exactly
// (object { target_stage, target_label, confidence, evidence, reasoning,
// mapping_id, from_stage }, tolerates a legacy raw string).
const aiSuggestion = computed(() => {
  if (suggestionResolved.value) return null;
  const raw = props.mapping?.ai_suggestion || props.conversation?.ai_suggestion;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return { target_label: raw };
    }
  }
  return raw;
});

const suggestionMappingId = computed(
  () => aiSuggestion.value?.mapping_id || props.mapping?.id
);

const showHealthPanel = computed(
  () => intentScore.value !== null || aiSuggestion.value !== null
);

const acceptSuggestion = async () => {
  if (resolvingSuggestion.value || !suggestionMappingId.value) return;
  resolvingSuggestion.value = true;
  try {
    await ConversationFunnelMappingsAPI.acceptSuggestion(
      props.conversation.id,
      suggestionMappingId.value
    );
    suggestionResolved.value = true;
    useAlert(t('KANBAN.AI_SUGGESTION.ACCEPTED'));
  } catch {
    useAlert(t('KANBAN.AI_SUGGESTION.ERROR'));
  } finally {
    resolvingSuggestion.value = false;
  }
};

const ignoreSuggestion = async () => {
  if (resolvingSuggestion.value || !suggestionMappingId.value) return;
  resolvingSuggestion.value = true;
  try {
    await ConversationFunnelMappingsAPI.rejectSuggestion(
      props.conversation.id,
      suggestionMappingId.value
    );
    suggestionResolved.value = true;
  } catch {
    useAlert(t('KANBAN.AI_SUGGESTION.ERROR'));
  } finally {
    resolvingSuggestion.value = false;
  }
};

// --- Checklist panel ----------------------------------------------------

const checklist = computed(() => props.mapping?.checklist || []);

const checklistProgress = computed(() => {
  const done = checklist.value.filter(item => item.done).length;
  return { done, total: checklist.value.length };
});

const togglingItemId = ref(null);

// Reuses the exact KanbanAPI.updateChecklistItem endpoint
// DealDetailsSidebar.vue's toggleChecklistItem() calls; emits `update` so
// the parent screen reloads the conversation/mapping (same pattern as
// DealAbout's saveCloseDate) instead of mutating the `mapping` prop directly.
const toggleChecklistItem = async item => {
  if (!props.funnel?.id || !props.conversation?.id || togglingItemId.value) {
    return;
  }
  togglingItemId.value = item.id;
  try {
    await KanbanAPI.updateChecklistItem(
      props.funnel.id,
      props.conversation.id,
      item.id,
      { done: !item.done }
    );
    emit('update');
  } catch {
    useAlert(t('KANBAN.DEAL.CHECKLIST_UPDATE_ERROR'));
  } finally {
    togglingItemId.value = null;
  }
};

// --- Metrics panel ----------------------------------------------------

const dealAgeDays = computed(() => {
  const created = normalizeDate(props.conversation?.created_at);
  if (!created || Number.isNaN(created.getTime())) return null;
  return Math.floor((Date.now() - created.getTime()) / 86400000);
});

// Anti-epoch guard: reject missing/unparsable/pre-2015 stage_entered_at
// (e.g. a DB default epoch value) instead of showing a huge/negative day
// count — falls back to 0.
const MIN_VALID_YEAR = 2015;
const daysInStage = computed(() => {
  const enteredAt = props.mapping?.stage_entered_at;
  if (!enteredAt) return 0;
  const entered = new Date(enteredAt);
  if (Number.isNaN(entered.getTime())) return 0;
  if (entered.getFullYear() < MIN_VALID_YEAR) return 0;
  return Math.floor((Date.now() - entered.getTime()) / 86400000);
});

const lastActivityAt = computed(() => {
  const date = normalizeDate(props.conversation?.last_activity_at);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date;
});

const formattedLastActivity = computed(() => {
  if (!lastActivityAt.value) return '';
  return lastActivityAt.value.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});

// --- Products & PIX panel ----------------------------------------------

const products = computed(
  () => props.conversation?.conversation_products || []
);

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency.value,
  }).format(parseFloat(value) || 0);

const totalProductsValue = computed(() =>
  products.value.reduce(
    (sum, product) => sum + (parseFloat(product.amount) || 0),
    0
  )
);

const formattedTotal = computed(() => formatCurrency(totalProductsValue.value));

const canGeneratePix = computed(
  () =>
    totalProductsValue.value > 0 &&
    !!props.funnel?.id &&
    !!props.conversation?.id
);

const isCreatingCharge = ref(false);
const pixResult = ref(null);
const pixNotConfigured = ref(false);
const pixErrorMessage = ref('');

// Reuses the exact KanbanAPI.createDealPayment(funnelId, {...}) endpoint
// DealDetailsSidebar.vue's PIX modal calls, inlined here instead of behind
// a modal.
const createPixCharge = async () => {
  if (!canGeneratePix.value || isCreatingCharge.value) return;
  isCreatingCharge.value = true;
  pixNotConfigured.value = false;
  pixErrorMessage.value = '';
  try {
    const response = await KanbanAPI.createDealPayment(props.funnel.id, {
      conversationId: props.conversation.id,
      value: totalProductsValue.value,
    });
    pixResult.value = response.data;
    useAlert(t('KANBAN.DEAL.PIX.SUCCESS'));
  } catch (error) {
    const status = error?.response?.status;
    const message = error?.response?.data?.error;
    if (status === 422) {
      pixNotConfigured.value = true;
      pixErrorMessage.value = message || t('KANBAN.DEAL.PIX.NOT_CONFIGURED');
    } else {
      useAlert(message || t('KANBAN.DEAL.PIX.ERROR'));
    }
  } finally {
    isCreatingCharge.value = false;
  }
};

const copyPixPayload = async () => {
  if (!pixResult.value?.pix_payload) return;
  try {
    await copyTextToClipboard(pixResult.value.pix_payload);
    useAlert(t('KANBAN.DEAL.PIX.COPIED'));
  } catch {
    useAlert(t('KANBAN.DEAL.PIX.COPY_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col w-full gap-4 p-4">
    <CardLayout v-if="showHealthPanel && showSection('ia')">
      <h3
        class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
      >
        <span class="i-lucide-sparkles text-sm text-n-iris-11" />
        {{ t('DEAL_RECORD.INSIGHTS.HEALTH_TITLE') }}
      </h3>

      <div
        v-if="intentScore !== null"
        class="flex items-center justify-between w-full"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('DEAL_RECORD.ABOUT.IA_SCORE') }}
        </span>
        <span
          class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium bg-n-iris-3 text-n-iris-11"
        >
          <span class="i-lucide-sparkles text-xs" />
          {{ intentScore }}
        </span>
      </div>

      <p
        v-if="intentScore !== null && intentReasoning"
        class="text-xs italic text-n-slate-10"
      >
        {{ intentReasoning }}
      </p>

      <div
        v-if="aiSuggestion"
        class="flex flex-col w-full gap-2 rounded-lg bg-n-iris-2 p-3"
      >
        <p class="text-sm text-n-iris-11">
          {{
            t('KANBAN.AI_SUGGESTION.MOVE_TO', {
              stage: aiSuggestion.target_label,
            })
          }}
        </p>
        <p v-if="aiSuggestion.evidence" class="text-xs italic text-n-slate-10">
          {{ aiSuggestion.evidence }}
        </p>
        <div class="flex gap-2">
          <Button
            size="sm"
            color="teal"
            :label="t('KANBAN.AI_SUGGESTION.ACCEPT')"
            :is-loading="resolvingSuggestion"
            @click="acceptSuggestion"
          />
          <Button
            size="sm"
            variant="faded"
            color="slate"
            :label="t('KANBAN.AI_SUGGESTION.IGNORE')"
            :disabled="resolvingSuggestion"
            @click="ignoreSuggestion"
          />
        </div>
      </div>
    </CardLayout>

    <CardLayout v-if="checklist.length && showSection('ia')">
      <h3
        class="flex items-center justify-between w-full text-sm font-medium text-n-slate-11"
      >
        <span>{{ t('KANBAN.DEAL.DETAILS.CHECKLIST') }}</span>
        <span class="text-xs font-normal text-n-slate-10 tabular-nums">
          {{ t('DEAL_RECORD.CHECKLIST.PROGRESS', checklistProgress) }}
        </span>
      </h3>

      <div class="flex flex-col w-full gap-0.5">
        <label
          v-for="item in checklist"
          :key="item.id"
          class="flex items-center w-full gap-2.5 px-1 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-1"
        >
          <Checkbox
            :model-value="item.done"
            :disabled="togglingItemId === item.id"
            @update:model-value="toggleChecklistItem(item)"
          />
          <span
            class="text-sm"
            :class="
              item.done ? 'text-n-slate-10 line-through' : 'text-n-slate-12'
            "
          >
            {{ item.text }}
          </span>
        </label>
      </div>
    </CardLayout>

    <CardLayout v-if="showSection('ia')">
      <h3
        class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
      >
        <span class="i-lucide-bar-chart-3 text-sm text-n-slate-10" />
        {{ t('DEAL_RECORD.INSIGHTS.METRICS_TITLE') }}
      </h3>

      <div class="flex flex-col w-full divide-y divide-n-weak">
        <div
          v-if="dealAgeDays !== null"
          class="flex items-center justify-between w-full py-1.5 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('DEAL_RECORD.INSIGHTS.DEAL_AGE') }}
          </span>
          <span class="text-xs font-medium text-n-slate-12 tabular-nums">
            {{ t('DEAL_RECORD.INSIGHTS.N_DAYS', { days: dealAgeDays }) }}
          </span>
        </div>

        <div
          class="flex items-center justify-between w-full py-1.5 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('DEAL_RECORD.INSIGHTS.DAYS_IN_STAGE') }}
          </span>
          <span class="text-xs font-medium text-n-slate-12 tabular-nums">
            {{ t('DEAL_RECORD.INSIGHTS.N_DAYS', { days: daysInStage }) }}
          </span>
        </div>

        <div
          v-if="formattedLastActivity"
          class="flex items-center justify-between w-full py-1.5 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('DEAL_RECORD.INSIGHTS.LAST_ACTIVITY') }}
          </span>
          <span class="text-xs font-medium text-n-slate-12">
            {{ formattedLastActivity }}
          </span>
        </div>
      </div>
    </CardLayout>

    <CardLayout v-if="showSection('produtos')">
      <h3
        class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
      >
        <span class="i-lucide-package text-sm text-n-slate-10" />
        {{ t('KANBAN.DEAL.DETAILS.PRODUCTS') }}
      </h3>

      <div v-if="products.length" class="flex flex-col w-full gap-1.5">
        <div
          v-for="product in products"
          :key="product.id"
          class="flex items-center justify-between rounded-lg bg-n-alpha-1 p-2"
        >
          <span class="text-sm text-n-slate-12">
            {{ product.name || product.product_name }}
            <span v-if="product.quantity > 1" class="text-n-slate-10">
              {{
                t('DEAL_RECORD.INSIGHTS.ITEM_QUANTITY', {
                  qty: product.quantity,
                })
              }}
            </span>
          </span>
          <span class="text-xs font-medium text-n-slate-12 tabular-nums">
            {{ formatCurrency(product.amount) }}
          </span>
        </div>

        <div class="flex items-center justify-between w-full pt-1">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('DEAL_RECORD.INSIGHTS.TOTAL') }}
          </span>
          <span class="text-base font-semibold text-n-slate-12 tabular-nums">
            {{ formattedTotal }}
          </span>
        </div>

        <div
          v-if="pixNotConfigured"
          class="rounded-lg bg-n-amber-3 p-3 text-xs text-n-amber-11"
        >
          {{ pixErrorMessage || t('KANBAN.DEAL.PIX.NOT_CONFIGURED') }}
        </div>

        <div
          v-else-if="pixResult"
          class="flex flex-col gap-2 rounded-lg bg-n-alpha-1 p-3"
        >
          <p class="text-xs text-n-slate-11">
            {{ t('KANBAN.DEAL.PIX.WAITING_PAYMENT') }}
          </p>
          <div v-if="pixResult.pix_payload" class="flex items-start gap-2">
            <p class="flex-1 break-all font-mono text-xs text-n-slate-11">
              {{ pixResult.pix_payload }}
            </p>
            <Button
              size="xs"
              variant="faded"
              color="teal"
              :label="t('KANBAN.DEAL.PIX.COPY')"
              @click="copyPixPayload"
            />
          </div>
        </div>

        <Button
          v-if="!pixResult"
          class="w-full"
          size="sm"
          color="teal"
          variant="faded"
          :label="t('KANBAN.DEAL.PIX.GENERATE_BUTTON')"
          :is-loading="isCreatingCharge"
          :disabled="!canGeneratePix"
          @click="createPixCharge"
        />
      </div>

      <p v-else class="text-sm text-n-slate-10">
        {{ t('DEAL_RECORD.INSIGHTS.PRODUCTS_EMPTY') }}
      </p>
    </CardLayout>
  </div>
</template>
